/**
 * Affiliate Blog v2 template — Angela-style "Shop the look" product grid
 * for articles tagged `blog_layout: looks_products_roundup`.
 */
import React from "react";
import { updateRefClick } from "../../../Utils/SupabaseServices/ContentDatabase";
import { E, useEdit } from "./InlineEdit";
import { useTemplateAnalyticsOptional } from "./TemplateAnalyticsContext";
import type { BlogRelatedRailItem } from "./BlogRelatedRail";

function ShopLookCard({
  item,
  index,
  userId,
}: {
  item: BlogRelatedRailItem;
  index: number;
  userId: string;
}) {
  const { editable } = useEdit();
  const analytics = useTemplateAnalyticsOptional();
  const interactive = Boolean(item.href) && !editable;

  const onActivate = () => {
    if (!interactive || !item.href) return;
    if (userId && item.id) {
      analytics?.trackShopClick({
        postId: item.id,
        name: item.title,
        sectionId: "affiliateShopLook",
        shopView: "category",
        contentCategory: item.subtitle,
      });
      void updateRefClick(item.id, userId);
    }
    window.open(item.href, "_blank", "noopener,noreferrer");
  };

  return (
    <article
      className={`mte-tpl-shop-look-card${
        interactive ? " mte-tpl-shop-look-card--interactive" : ""
      }`}
      role={interactive ? "link" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? onActivate : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onActivate();
              }
            }
          : undefined
      }
    >
      <div className="mte-tpl-shop-look-card-media">
        {item.image ? (
          <img
            src={item.image}
            alt=""
            className="mte-tpl-img-filter mte-tpl-shop-look-card-img"
          />
        ) : (
          <div className="mte-tpl-shop-look-card-ph" aria-hidden />
        )}
        <span className="mte-tpl-shop-look-card-num" aria-hidden>
          {String(index + 1).padStart(2, "0")}
        </span>
      </div>
      <div className="mte-tpl-shop-look-card-body">
        {item.subtitle ? (
          <p className="mte-tpl-shop-look-card-brand">
            <E path={`shopLook.${item.id}.brand`}>{item.subtitle}</E>
          </p>
        ) : null}
        <h3 className="mte-tpl-shop-look-card-title">
          <E path={`shopLook.${item.id}.title`}>{item.title || "Product"}</E>
        </h3>
        {item.href ? (
          <span className="mte-tpl-shop-look-card-cta">Shop now →</span>
        ) : null}
      </div>
    </article>
  );
}

export function AffiliateShopLookTemplate({
  items,
  userId,
  loading,
  eyebrow = "Shop the look",
  heading = "Featured products",
}: {
  items: BlogRelatedRailItem[];
  userId: string;
  loading?: boolean;
  eyebrow?: string;
  heading?: string;
}) {
  if (loading && items.length === 0) {
    return (
      <div className="mte-tpl-shop-look" data-tpl-shop-look="" aria-busy="true">
        <div className="mte-tpl-shop-look-head">
          <p className="mte-tpl-shop-look-kicker">{eyebrow}</p>
          <h2 className="mte-tpl-shop-look-title">{heading}</h2>
        </div>
        <div className="mte-tpl-shop-look-grid">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="mte-tpl-shop-look-skel" />
          ))}
        </div>
      </div>
    );
  }

  if (!items.length) return null;

  return (
    <ShopLookCarousel
      items={items}
      userId={userId}
      eyebrow={eyebrow}
      heading={heading}
    />
  );
}

function ShopLookCarousel({
  items,
  userId,
  eyebrow,
  heading,
}: {
  items: BlogRelatedRailItem[];
  userId: string;
  eyebrow: string;
  heading: string;
}) {
  const scrollerRef = React.useRef<HTMLDivElement>(null);
  const scrollByCard = (dir: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>(".mte-tpl-shop-look-card");
    const step = (card?.offsetWidth || 220) + 16;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <div className="mte-tpl-shop-look" data-tpl-shop-look="">
      <div className="mte-tpl-shop-look-head">
        <div>
          <p className="mte-tpl-shop-look-kicker">{eyebrow}</p>
          <h2 className="mte-tpl-shop-look-title">{heading}</h2>
        </div>
        {items.length > 1 ? (
          <div className="mte-tpl-shop-look-nav">
            <button
              type="button"
              className="mte-tpl-shop-look-nav-btn"
              aria-label="Previous products"
              onClick={() => scrollByCard(-1)}
            >
              ‹
            </button>
            <button
              type="button"
              className="mte-tpl-shop-look-nav-btn"
              aria-label="Next products"
              onClick={() => scrollByCard(1)}
            >
              ›
            </button>
          </div>
        ) : null}
      </div>
      <div
        ref={scrollerRef}
        className="mte-tpl-shop-look-grid"
        role="region"
        aria-label={heading}
      >
        {items.map((item, index) => (
          <ShopLookCard
            key={item.id || `${item.title}-${index}`}
            item={item}
            index={index}
            userId={userId}
          />
        ))}
      </div>
    </div>
  );
}
